import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/services/authService'

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref([])
  const socket = ref(null)
  const loading = ref(false)
  let closedManually = false
  let reconnectTimeout = null
  let isReconnecting = false
  let connectionAttempts = 0
  const MAX_ATTEMPTS = 5
  const firstWebSocketConnect = ref(true)

  const initWebSocket = () => {
    if (firstWebSocketConnect.value) {
      loading.value = true
    }
    if (socket.value && typeof socket.value.close === 'function') {
      closedManually = false
      if (!isReconnecting) {
        isReconnecting = true
        socket.value.close()
      }
      return
    }

    closedManually = false
    isReconnecting = false
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const wsUrl = `${protocol}://${window.location.host}/socket/notifications/`

    try {
      socket.value = new WebSocket(wsUrl)
      connectionAttempts++

      socket.value.onopen = () => {
        loading.value = false
        firstWebSocketConnect.value = false
        connectionAttempts = 0 // reset ao conectar
      }

      socket.value.onmessage = ({ data }) => {
        const msg = JSON.parse(data)
        const newNotification = {
          id: msg.id,
          title: msg.titulo,
          message: msg.mensagem,
          time: msg.time ? new Date(msg.time) : new Date(),
          read: msg.status === 'lida' ? true : (msg.status !== 'nao_lida' ? true : false),
          type: msg.evento,
          link: msg.link
        }
        const exists = notifications.value.find(n => n.id === newNotification.id)
        if (msg.status === 'lida') {
          if (exists) {
            exists.read = true
          } else {
            notifications.value.push(newNotification)
          }
        } else if (msg.status === 'nao_lida') {
          if (exists) {
            exists.read = false
          } else {
            notifications.value.unshift(newNotification)
            const audio = new Audio('/sounds/notification.mp3')
            audio.play()
          }
        }
      }

      socket.value.onclose = () => {
        loading.value = false
        if (!closedManually) {
          if (connectionAttempts < MAX_ATTEMPTS) {
            console.warn('🔴 WebSocket desconectado. Tentando reconectar em 5s...')
            if (!reconnectTimeout) {
              reconnectTimeout = setTimeout(() => {
                reconnectTimeout = null
                initWebSocket()
              }, 5000)
            }
          } else {
            console.error('❌ Não foi possível conectar ao WebSocket após várias tentativas.')
          }
        } 
        isReconnecting = false
        socket.value = null
      }

      socket.value.onerror = (error) => {
        loading.value = false
        if (connectionAttempts < MAX_ATTEMPTS) {
          console.warn('⚠️ WebSocket erro, tentando novamente...')
        } else {
          console.error('❌ WebSocket erro persistente:', error)
        }
        socket.value.close()
      }
    } catch (error) {
      loading.value = false
      console.error('❌ Erro ao inicializar WebSocket:', error)
    }
  }

  const closeWebSocket = () => {
    closedManually = true
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }
    if (socket.value && typeof socket.value.close === 'function') {
      socket.value.close()
    }
    socket.value = null
  }


  const markAsRead = async (notification) => {
    if (notification.read) return
    await apiClient.post(`/api/v1/notifications/marcar-como-lida/${notification.id}/`, {
      read: true
    })
    notification.read = true
  }

  const markAllAsRead = async () => {
    await apiClient.post('/api/v1/notifications/marcar-todas-como-lidas/')
    notifications.value.forEach(n => n.read = true)
  }

  const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)
  const readCount = computed(() => notifications.value.filter(n => n.read).length)
  const hasUnread = computed(() => unreadCount.value > 0)

  return {
    notifications,
    socket,
    loading,
    unreadCount,
    readCount,
    hasUnread,
    initWebSocket,
    closeWebSocket,
    markAsRead,
    markAllAsRead,
    firstWebSocketConnect,
  }
}, {
  persist: {
    paths: [
      'notifications',
      'loading',
      // outros campos serializáveis, mas NÃO 'socket'
    ]
  }
})
